import { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { saveTenantSettings } from '../../services/catalog/supabaseCatalog'
import type { LoyaltySettings } from '../../types'
import './LoyaltyPanel.css'

/**
 * Ayarlar > Sadakat (30 Eki 2026): damga kuralını işletme belirler. Damgayı
 * sunucu bu kuralla sayar (supabase/sadakat.sql, tenants.loyalty).
 */
export function LoyaltyPanel() {
  const { tenantId, loyalty } = useAuth()
  const [draft, setDraft] = useState<LoyaltySettings>(loyalty)
  const [amount, setAmount] = useState(String(loyalty.amountKurus / 100))
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    setDraft(loyalty)
    setAmount(String(loyalty.amountKurus / 100))
  }, [loyalty])

  const amountKurus = Math.round(Number(amount.replace(',', '.')) * 100)
  const valid =
    draft.target >= 1 &&
    draft.target <= 50 &&
    draft.rewardPercent >= 1 &&
    draft.rewardPercent <= 100 &&
    (draft.per === 'visit' || (Number.isFinite(amountKurus) && amountKurus >= 100))
  const changed =
    draft.per !== loyalty.per ||
    draft.target !== loyalty.target ||
    draft.rewardPercent !== loyalty.rewardPercent ||
    (draft.per === 'amount' && amountKurus !== loyalty.amountKurus)

  const save = async () => {
    if (!tenantId || !valid) return
    setBusy(true)
    setMsg(null)
    try {
      await saveTenantSettings(tenantId, { loyalty: { ...draft, amountKurus: draft.per === 'amount' ? amountKurus : loyalty.amountKurus } })
      setMsg({ ok: true, text: 'Kaydedildi.' })
    } catch {
      setMsg({ ok: false, text: 'Kaydedilemedi. İnternet bağlantınızı kontrol edin.' })
    } finally {
      setBusy(false)
    }
  }

  const rule =
    draft.per === 'visit'
      ? 'Her ödemede 1 damga'
      : `Her ${amount || '?'} ₺ harcamaya 1 damga`

  return (
    <div className="loyset">
      <p className="loyset__lead">
        Müşteriye kasada bir sadakat kartı açılır, 4 haneli bir kod alır. Her ödemede kodunu, telefonunu ya da adını söyler, damgası birikir;
        kart dolunca hesabına indirim uygulanır. Müşteri kartını karekodla kendi telefonunda görür.
      </p>

      <div className="loyset__field">
        <span>Damga</span>
        <div className="loyset__seg" role="radiogroup" aria-label="Damga kuralı">
          {(
            [
              ['visit', 'Her ödemede 1 damga'],
              ['amount', 'Tutar başına'],
            ] as const
          ).map(([v, t]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={draft.per === v}
              className={draft.per === v ? 'is-on' : ''}
              onClick={() => setDraft({ ...draft, per: v })}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {draft.per === 'amount' && (
        <label className="loyset__field">
          <span>Kaç liraya 1 damga</span>
          <div className="loyset__unit">
            <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))} />
            <b>₺</b>
          </div>
        </label>
      )}

      <div className="loyset__row">
        <label className="loyset__field">
          <span>Kart kaç damgada dolar</span>
          <input
            inputMode="numeric"
            value={draft.target}
            onChange={(e) => setDraft({ ...draft, target: Math.min(50, Number(e.target.value.replace(/\D/g, '')) || 0) })}
          />
        </label>
        <label className="loyset__field">
          <span>Kart dolunca indirim</span>
          <div className="loyset__unit">
            <b>%</b>
            <input
              inputMode="numeric"
              value={draft.rewardPercent}
              onChange={(e) =>
                setDraft({ ...draft, rewardPercent: Math.min(100, Number(e.target.value.replace(/\D/g, '')) || 0) })
              }
            />
          </div>
        </label>
      </div>

      <div className="loyset__preview">
        <span>Özet</span>
        <b>
          {rule}. {draft.target || '?'} damgada %{draft.rewardPercent || '?'} indirim.
        </b>
      </div>

      <div className="loyset__foot">
        {msg && <span className={`loyset__msg ${msg.ok ? 'is-ok' : 'is-bad'}`}>{msg.text}</span>}
        <button type="button" className="btn btn--primary" disabled={!valid || !changed || busy} onClick={() => void save()}>
          {busy ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </div>
    </div>
  )
}
